import PropTypes from 'prop-types';
import React from 'react';
import { Row, Col, Card, Table } from 'react-bootstrap';
import Flex from 'components/common/Flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { getColor } from 'helpers/utils';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import BasicECharts from 'components/common/BasicEChart';
import FalconCardHeader from 'components/common/FalconCardHeader';

echarts.use([TitleComponent, TooltipComponent, GridComponent, PieChart, CanvasRenderer]);

const getOptions = (data, radius) => ({
  color: data.map(d => getColor(d.color)),
  tooltip: {
    padding: [7, 10],
    transitionDuration: 0,
    backgroundColor: getColor('100'),
    borderColor: getColor('300'),
    textStyle: { color: getColor('dark') },
    borderWidth: 1,
    formatter: params =>
      `<strong>${params.data.name}:</strong> ${params.data.value} (${params.percent}%)`
  },
  series: [
    {
      type: 'pie',
      radius,
      itemStyle: {
        borderWidth: 1,
        borderColor: getColor('card-bg')
      },
      label: { show: false },
      data: data.map(d => ({ name: d.name, value: d.value }))
    }
  ]
});

const PieItem = ({ item, index, total }) => {
  const { name, color, value } = item;
  const percentage = total ? ((value * 100) / total).toFixed(1) : 0;

  return (
    <Flex
      alignItems="center"
      justifyContent="between"
      className={`fw-semi-bold fs--2 ${index === 0 && 'mt-3'}`}
    >
      <p className="mb-1">
        <FontAwesomeIcon icon="circle" className={`me-2 text-${color}`} />
        {name}
      </p>
      <div>{percentage}%</div>
    </Flex>
  );
};

PieItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired
  }),
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired
};

export default function DespatchSummaryWidget({ summary }) {
  const { route, totals, stages } = summary;

  const greenKeys = [
    'framesRTL',
    'boxesRTL',
    'ancillsRTL',
    'weldedCillsRTL',
    'oneOffExtrasRTL'
  ];

  const totalSum = Object.values(totals).reduce((sum, v) => sum + (v || 0), 0);
  const greenSum = greenKeys.reduce((sum, key) => sum + (stages[key] || 0), 0);
  const amberSum = stages.framesAtEOL || 0;
  const redSum = Math.max(totalSum - (greenSum + amberSum), 0);

  const chartData = [
    { name: 'RTL', color: 'success', value: greenSum },
    { name: 'EOL', color: 'warning', value: amberSum },
    { name: 'Anything before EOL', color: 'danger', value: redSum }
  ];

  const total = chartData.reduce((sum, d) => sum + d.value, 0);

  const tableHeaders = [
    'Frames (Stillage)',
    'Hardware',
    'Ancill',
    'Welded Cills',
    'OOE',
    'Glass Stillages'
  ];

  const tableValues = [
    `${stages.framesRTL || 0}/${totals.frames || 0}`,
    `${stages.boxesRTL || 0}/${totals.boxes || 0}`,
    `${stages.ancillsRTL || 0}/${totals.ancills || 0}`,
    `${stages.weldedCillsRTL || 0}/${totals.weldedCills || 0}`,
    `${stages.oneOffExtrasRTL || 0}/${totals.oneOffExtras || 0}`,
    stages.glassStillages ? 'Yes' : 'No'
  ];

  return (
    <Card className="border rounded-3 mb-3 bg-light-subtle p-3">
      <FalconCardHeader title={route} titleTag="h5" className="p-0 mb-3" />
      <Card.Body className="p-0">
        <div className="mb-3">
          <Table bordered responsive size="sm" className="text-center align-middle mb-2">
            <thead className="bg-secondary text-white">
              <tr>
                {tableHeaders.map((header, i) => (
                  <th key={i} className="py-2">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {tableValues.map((val, i) => (
                  <td key={i} className="py-2">{val}</td>
                ))}
              </tr>
            </tbody>
          </Table>
        </div>

        <Row className="justify-content-between g-0 p-2">
          <Col className="pe-2">
            {chartData.map((item, index) => (
              <PieItem key={index} item={item} index={index} total={total} />
            ))}
            <div className="fw-bold mt-2 text-primary">
              Total: {totalSum.toLocaleString()}
            </div>
          </Col>
          <Col xs="auto">
            <BasicECharts
              echarts={echarts}
              options={getOptions(chartData, ['100%'])}
              style={{ width: '7rem', height: '7rem' }}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

DespatchSummaryWidget.propTypes = {
  summary: PropTypes.shape({
    route: PropTypes.string.isRequired,
    totals: PropTypes.object.isRequired,
    stages: PropTypes.object.isRequired
  }).isRequired
};
